import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { fmtINR } from "@/lib/format";
import { toast } from "sonner";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, arrayMove, useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend } from "recharts";
import {
  Plus, Trash2, GripVertical, Save, RotateCcw, Play, X, Layers, Calendar,
  Settings2, Wand2, Download, BarChart3, LineChart as LineIcon, TableIcon, Sigma,
} from "lucide-react";

/**
 * PivotBuilder — Iter-177 agile MIS.
 *
 * Left panel  : row source (line items / accounts / segments / custom dim)
 *              + row list (drag-reorder, add/remove)
 * Middle panel: column list — each is a period preset (with anchor override)
 *              or custom date range
 * Right panel : filters (segment / party / custom dim value) + group_by
 * Below       : "Run" button + rendered matrix table + save-as-view
 */

function SortablePill({ id, label, onRemove, dataTestId }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <div ref={setNodeRef} style={style}
         className="flex items-center gap-1.5 border rounded-sm px-2 py-1.5 bg-card text-xs group hover:border-accent"
         data-testid={dataTestId}>
      <button {...attributes} {...listeners}
              className="text-muted-foreground hover:text-accent cursor-grab">
        <GripVertical className="w-3 h-3" />
      </button>
      <span className="flex-1 truncate">{label}</span>
      <button onClick={onRemove}
                className="text-muted-foreground hover:text-red-500 opacity-0 group-hover:opacity-100">
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}

export default function PivotBuilder() {
  const [presets, setPresets] = useState([]);
  const [lineItems, setLineItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [bizSegs, setBizSegs] = useState([]);
  const [geoSegs, setGeoSegs] = useState([]);
  const [customDims, setCustomDims] = useState([]);
  const [savedSpecs, setSavedSpecs] = useState([]);

  const [rowSource, setRowSource] = useState("line_items");
  const [rowIds, setRowIds] = useState([]);
  const [customDimId, setCustomDimId] = useState("");
  const [columns, setColumns] = useState([
    { id: "col_1", preset: "ytd" },
    { id: "col_2", preset: "py_ytd" },
    { id: "col_3", preset: "cm" },
  ]);
  const [groupBy, setGroupBy] = useState("");
  const [anchorDate, setAnchorDate] = useState(new Date().toISOString().slice(0, 10));
  const [filterBiz, setFilterBiz] = useState("");
  const [filterGeo, setFilterGeo] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const [openManageCats, setOpenManageCats] = useState(false);
  const [openManageDims, setOpenManageDims] = useState(false);
  const [openSaveSpec, setOpenSaveSpec] = useState(false);
  const [newSpecName, setNewSpecName] = useState("");
  const [viewMode, setViewMode] = useState("table");        // table | bar | line

  const sensors = useSensors(useSensor(PointerSensor, {
    activationConstraint: { distance: 4 },
  }));

  const load = async () => {
    try {
      const [pr, li, accs, bz, ge, dims, sp] = await Promise.all([
        api.get("/mis/pivot/periods"),
        api.get("/mis/pivot/line-items"),
        api.get("/accounts"),
        api.get("/segments/business"),
        api.get("/segments/geo"),
        api.get("/mis/pivot/dimensions"),
        api.get("/mis/pivot/specs"),
      ]);
      setPresets(pr.data.presets || []);
      setLineItems(li.data.items || []);
      setAccounts(accs.data || []);
      setBizSegs(bz.data || []);
      setGeoSegs(ge.data || []);
      setCustomDims(dims.data.items || []);
      setSavedSpecs(sp.data.items || []);

      // Seed defaults if none
      if ((li.data.items || []).length === 0) {
        try {
          await api.post("/mis/pivot/line-items/seed-defaults");
          const li2 = await api.get("/mis/pivot/line-items");
          setLineItems(li2.data.items || []);
          const first = (li2.data.items || []).slice(0, 5).map(x => x.id);
          setRowIds(first);
          toast.success("Seeded 7 default line-item categories — customise them anytime");
        } catch {}
      } else if (rowIds.length === 0) {
        setRowIds((li.data.items || []).slice(0, 5).map(x => x.id));
      }

      // Auto-apply default spec if any
      const def = (sp.data.items || []).find(s => s.is_default);
      if (def) applySpec(def);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load pivot builder");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const applySpec = (s) => {
    setRowSource(s.row_source || "line_items");
    setRowIds(s.row_ids || []);
    setColumns(s.columns?.length ? s.columns : columns);
    setGroupBy(s.group_by || "");
    setAnchorDate(s.anchor_date || anchorDate);
    setFilterBiz(s.filter?.business_segment_id || "");
    setFilterGeo(s.filter?.geo_segment_id || "");
    setCustomDimId(s.custom_dimension_id || "");
  };

  const currentSourceItems = useMemo(() => {
    if (rowSource === "line_items") return lineItems.map(x => ({ id: x.id, label: x.label || x.name }));
    if (rowSource === "accounts")   return accounts.map(x => ({ id: x.id, label: `${x.code} · ${x.name}` }));
    if (rowSource === "segments")   return [...bizSegs, ...geoSegs].map(x => ({ id: x.id, label: `${x.code || ""} ${x.name}`.trim() }));
    if (rowSource === "custom_dimension") {
      const d = customDims.find(x => x.id === customDimId);
      return (d?.values || []).map(v => ({ id: v.id, label: v.label }));
    }
    return [];
  }, [rowSource, lineItems, accounts, bizSegs, geoSegs, customDims, customDimId]);

  const availableToAdd = currentSourceItems.filter(x => !rowIds.includes(x.id));

  const runPivot = async () => {
    setBusy(true);
    try {
      const spec = {
        row_source: rowSource,
        row_ids: rowIds,
        columns: columns.map(c => ({ id: c.id, preset: c.preset,
                                        from_date: c.from_date, to_date: c.to_date,
                                        label: c.label })),
        group_by: groupBy || null,
        filter: {
          ...(filterBiz && { business_segment_id: filterBiz }),
          ...(filterGeo && { geo_segment_id: filterGeo }),
        },
        anchor_date: anchorDate,
        custom_dimension_id: customDimId || null,
      };
      const r = await api.post("/mis/pivot/run", spec);
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pivot run failed");
    }
    setBusy(false);
  };

  const saveSpec = async () => {
    if (!newSpecName.trim()) return toast.error("Give this view a name");
    try {
      await api.post("/mis/pivot/specs", {
        name: newSpecName.trim(),
        row_source: rowSource, row_ids: rowIds,
        columns, group_by: groupBy || null,
        filter: {
          ...(filterBiz && { business_segment_id: filterBiz }),
          ...(filterGeo && { geo_segment_id: filterGeo }),
        },
        anchor_date: anchorDate,
        custom_dimension_id: customDimId || null,
      });
      toast.success("Saved");
      setOpenSaveSpec(false); setNewSpecName("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const downloadCSV = () => {
    if (!result?.rows) return;
    const cols = result.columns.map(c => c.label);
    const csv = ["Row," + cols.map(c => `"${c}"`).join(",")].concat(
      result.rows.map(r =>
        `"${r.label}",` + result.columns.map(c => result.matrix[r.key]?.[c.id] ?? 0).join(",")
      )
    ).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `MIS_Pivot_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const downloadXLSX = async () => {
    if (!result?.rows) return;
    try {
      const spec = {
        row_source: rowSource, row_ids: rowIds, columns,
        group_by: groupBy || null,
        filter: {
          ...(filterBiz && { business_segment_id: filterBiz }),
          ...(filterGeo && { geo_segment_id: filterGeo }),
        },
        anchor_date: anchorDate, custom_dimension_id: customDimId || null,
      };
      const r = await api.post("/mis/pivot/run.xlsx", spec, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `MIS_Pivot_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel downloaded — SUM formulas ready to drill");
    } catch (e) {
      toast.error("Excel export failed");
    }
  };

  // Chart data — transpose the matrix so recharts renders one bar per row
  const chartData = useMemo(() => {
    if (!result?.rows || !result.columns) return [];
    return result.columns.map((c) => {
      const row = { name: c.label };
      result.rows.forEach((r) => { row[r.label] = result.matrix[r.key]?.[c.id] ?? 0; });
      return row;
    });
  }, [result]);
  const chartPalette = ["#0F766E", "#7C3AED", "#F59E0B", "#DC2626", "#2563EB",
                          "#0891B2", "#DB2777", "#65A30D", "#EA580C", "#4F46E5"];

  return (
    <div className="space-y-4" data-testid="pivot-builder">
      {/* Top action bar */}
      <div className="flex items-center flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Label className="text-[10px] font-mono uppercase text-muted-foreground">Anchor date</Label>
          <Input type="date" value={anchorDate}
                   onChange={(e) => setAnchorDate(e.target.value)}
                   className="rounded-sm h-8 w-40 text-xs" data-testid="pivot-anchor" />
          <Label className="text-[10px] font-mono uppercase text-muted-foreground ml-3">View</Label>
          <Select value="__none" onValueChange={(v) => {
            if (v === "__none") return;
            const s = savedSpecs.find(x => x.id === v);
            if (s) applySpec(s);
          }}>
            <SelectTrigger className="rounded-sm h-8 min-w-[200px] text-xs" data-testid="pivot-view-select">
              <SelectValue placeholder="Load saved view…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none">— pick —</SelectItem>
              {savedSpecs.map(s => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}{s.is_default ? " · default" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={() => setOpenManageCats(true)}
                  className="rounded-sm h-8 gap-1" data-testid="pivot-manage-cats">
          <Layers className="w-3 h-3" /> Line-items
        </Button>
        <Button variant="outline" size="sm" onClick={() => setOpenManageDims(true)}
                  className="rounded-sm h-8 gap-1" data-testid="pivot-manage-dims">
          <Wand2 className="w-3 h-3" /> Custom dimensions
        </Button>
        <Button variant="outline" size="sm" onClick={() => setOpenSaveSpec(true)}
                  className="rounded-sm h-8 gap-1" data-testid="pivot-save">
          <Save className="w-3 h-3" /> Save view
        </Button>
        <Button size="sm" onClick={runPivot} disabled={busy}
                  className="rounded-sm h-8 gap-1 bg-accent hover:bg-accent/90 text-white"
                  data-testid="pivot-run">
          <Play className="w-3 h-3" /> {busy ? "Running…" : "Run"}
        </Button>
      </div>

      {/* Three-column builder */}
      <div className="grid md:grid-cols-3 gap-3">
        {/* Rows */}
        <Card className="p-3 rounded-sm">
          <div className="text-[10px] font-mono uppercase text-accent mb-2">Rows</div>
          <div className="flex gap-2 mb-2">
            <Select value={rowSource} onValueChange={(v) => { setRowSource(v); setRowIds([]); }}>
              <SelectTrigger className="rounded-sm h-8 text-xs" data-testid="pivot-row-source">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="line_items">Line-item categories</SelectItem>
                <SelectItem value="accounts">Individual accounts</SelectItem>
                <SelectItem value="segments">Segments (biz + geo)</SelectItem>
                <SelectItem value="custom_dimension">Custom dimension</SelectItem>
              </SelectContent>
            </Select>
            {rowSource === "custom_dimension" && (
              <Select value={customDimId} onValueChange={(v) => { setCustomDimId(v); setRowIds([]); }}>
                <SelectTrigger className="rounded-sm h-8 text-xs" data-testid="pivot-custom-dim">
                  <SelectValue placeholder="Pick a dimension" />
                </SelectTrigger>
                <SelectContent>
                  {customDims.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>

          <DndContext sensors={sensors} collisionDetection={closestCenter}
                       onDragEnd={(ev) => {
                          const { active, over } = ev;
                          if (!over || active.id === over.id) return;
                          const oldIx = rowIds.indexOf(active.id);
                          const newIx = rowIds.indexOf(over.id);
                          setRowIds(arrayMove(rowIds, oldIx, newIx));
                       }}>
            <SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
              <div className="space-y-1.5 min-h-[80px]">
                {rowIds.map((id) => {
                  const item = currentSourceItems.find(x => x.id === id);
                  return (
                    <SortablePill key={id} id={id}
                                      label={item?.label || id}
                                      onRemove={() => setRowIds(rowIds.filter(x => x !== id))}
                                      dataTestId={`pivot-row-${id}`} />
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>

          {availableToAdd.length > 0 && (
            <div className="mt-3 border-t pt-2">
              <div className="text-[10px] font-mono uppercase text-muted-foreground mb-1">
                Add ({availableToAdd.length})
              </div>
              <div className="max-h-40 overflow-auto space-y-1">
                {availableToAdd.slice(0, 30).map((x) => (
                  <button key={x.id}
                             onClick={() => setRowIds([...rowIds, x.id])}
                             className="w-full text-left text-xs px-2 py-1 rounded-sm hover:bg-accent/10"
                             data-testid={`pivot-add-row-${x.id}`}>
                    <Plus className="w-3 h-3 inline mr-1" />{x.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </Card>

        {/* Columns */}
        <Card className="p-3 rounded-sm">
          <div className="text-[10px] font-mono uppercase text-accent mb-2">Columns (period comparisons)</div>
          <DndContext sensors={sensors} collisionDetection={closestCenter}
                       onDragEnd={(ev) => {
                          const { active, over } = ev;
                          if (!over || active.id === over.id) return;
                          const ids = columns.map(c => c.id);
                          const oldIx = ids.indexOf(active.id);
                          const newIx = ids.indexOf(over.id);
                          setColumns(arrayMove(columns, oldIx, newIx));
                       }}>
            <SortableContext items={columns.map(c => c.id)}
                              strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {columns.map((c, i) => (
                  <SortableColumnRow key={c.id} column={c}
                    presets={presets}
                    onChange={(nc) => setColumns(columns.map((x) => (x.id === c.id ? nc : x)))}
                    onRemove={() => setColumns(columns.filter(x => x.id !== c.id))} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
          <Button size="sm" variant="outline" onClick={() => setColumns([
            ...columns, { id: `col_${Date.now()}`, preset: "cm" },
          ])} className="rounded-sm h-7 gap-1 mt-2 w-full" data-testid="pivot-add-col">
            <Plus className="w-3 h-3" /> Add column
          </Button>
        </Card>

        {/* Filter + group-by */}
        <Card className="p-3 rounded-sm">
          <div className="text-[10px] font-mono uppercase text-accent mb-2">Filter &amp; Group-by</div>
          <div className="space-y-2">
            <div>
              <Label className="text-[10px] font-mono uppercase text-muted-foreground">Group columns by</Label>
              <Select value={groupBy || "__none"} onValueChange={(v) => setGroupBy(v === "__none" ? "" : v)}>
                <SelectTrigger className="rounded-sm h-8 text-xs mt-1" data-testid="pivot-groupby">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">— none (single-period columns) —</SelectItem>
                  <SelectItem value="business">Business segment</SelectItem>
                  <SelectItem value="geo">Geography</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-[10px] text-muted-foreground mt-1">
                Multiplies every column across the picked dimension.
              </div>
            </div>
            <div>
              <Label className="text-[10px] font-mono uppercase text-muted-foreground">Filter · Business</Label>
              <Select value={filterBiz || "__none"} onValueChange={(v) => setFilterBiz(v === "__none" ? "" : v)}>
                <SelectTrigger className="rounded-sm h-8 text-xs mt-1" data-testid="pivot-filter-biz">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">All</SelectItem>
                  {bizSegs.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] font-mono uppercase text-muted-foreground">Filter · Geo</Label>
              <Select value={filterGeo || "__none"} onValueChange={(v) => setFilterGeo(v === "__none" ? "" : v)}>
                <SelectTrigger className="rounded-sm h-8 text-xs mt-1" data-testid="pivot-filter-geo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">All</SelectItem>
                  {geoSegs.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>
      </div>

      {/* Result matrix */}
      {result && (
        <Card className="p-0 rounded-sm overflow-hidden">
          <div className="flex items-center justify-between p-2 border-b bg-muted/30 flex-wrap gap-2">
            <div className="text-xs">
              <b>{result.rows.length}</b> rows × <b>{result.columns.length}</b> cols
              {result.meta?.group_by && ` · grouped by ${result.meta.group_by}`}
            </div>
            <div className="flex items-center gap-1">
              {[
                ["table", "Table", TableIcon],
                ["bar", "Bar", BarChart3],
                ["line", "Line", LineIcon],
              ].map(([mode, label, Icon]) => (
                <Button key={mode} size="sm"
                          variant={viewMode === mode ? "default" : "outline"}
                          onClick={() => setViewMode(mode)}
                          className={`rounded-sm h-7 text-xs gap-1 ${viewMode === mode ? "bg-accent text-white" : ""}`}
                          data-testid={`pivot-view-${mode}`}>
                  <Icon className="w-3 h-3" /> {label}
                </Button>
              ))}
              <Button size="sm" variant="outline" onClick={downloadCSV}
                        className="rounded-sm h-7 gap-1 ml-2" data-testid="pivot-csv">
                <Download className="w-3 h-3" /> CSV
              </Button>
              <Button size="sm" variant="outline" onClick={downloadXLSX}
                        className="rounded-sm h-7 gap-1 border-emerald-500 text-emerald-700"
                        data-testid="pivot-xlsx">
                <Download className="w-3 h-3" /> Excel (formulas)
              </Button>
            </div>
          </div>
          {viewMode === "table" && (
            <div className="overflow-x-auto max-h-[65vh]">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 sticky top-0">
                  <tr>
                    <th className="text-left p-2 uppercase text-[10px] font-mono">Row</th>
                    {result.columns.map((c) => (
                      <th key={c.id} className="text-right p-2 uppercase text-[10px] font-mono"
                            data-testid={`pivot-hdr-${c.id}`}>
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.rows.map((r) => (
                    <tr key={r.key} className="border-t hover:bg-accent/5"
                          data-testid={`pivot-body-${r.key}`}>
                      <td className="p-2 font-medium">{r.label}</td>
                      {result.columns.map((c) => (
                        <td key={c.id} className="text-right p-2 tabular-nums">
                          {fmtINR(result.matrix[r.key]?.[c.id])}
                        </td>
                      ))}
                    </tr>
                  ))}
                  <tr className="border-t bg-blue-50 font-semibold">
                    <td className="p-2">Column totals</td>
                    {result.columns.map((c) => (
                      <td key={c.id} className="text-right p-2 tabular-nums">
                        {fmtINR(result.column_totals[c.id])}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          {viewMode === "bar" && (
            <div className="p-3" data-testid="pivot-chart-bar">
              <ResponsiveContainer width="100%" height={360}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" fontSize={10} />
                  <YAxis fontSize={10} tickFormatter={(v) => new Intl.NumberFormat("en-IN", {notation:"compact"}).format(v)} />
                  <Tooltip formatter={(v) => fmtINR(v)}
                              contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 4, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {result.rows.map((r, i) => (
                    <Bar key={r.key} dataKey={r.label}
                            fill={chartPalette[i % chartPalette.length]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          {viewMode === "line" && (
            <div className="p-3" data-testid="pivot-chart-line">
              <ResponsiveContainer width="100%" height={360}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" fontSize={10} />
                  <YAxis fontSize={10} tickFormatter={(v) => new Intl.NumberFormat("en-IN", {notation:"compact"}).format(v)} />
                  <Tooltip formatter={(v) => fmtINR(v)}
                              contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 4, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {result.rows.map((r, i) => (
                    <Line key={r.key} type="monotone" dataKey={r.label}
                             stroke={chartPalette[i % chartPalette.length]}
                             strokeWidth={2} dot={{ r: 3 }} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      )}

      {/* Manage categories dialog */}
      <ManageLineItemsDialog
        open={openManageCats} onOpenChange={setOpenManageCats}
        items={lineItems} accounts={accounts}
        onChanged={load} />

      {/* Manage custom dimensions dialog */}
      <ManageDimensionsDialog
        open={openManageDims} onOpenChange={setOpenManageDims}
        items={customDims} accounts={accounts} onChanged={load} />

      {/* Save-spec dialog */}
      <Dialog open={openSaveSpec} onOpenChange={setOpenSaveSpec}>
        <DialogContent className="rounded-sm sm:max-w-md">
          <DialogHeader><DialogTitle>Save this pivot view</DialogTitle></DialogHeader>
          <Input value={newSpecName} onChange={(e) => setNewSpecName(e.target.value)}
                   placeholder="e.g. CFO monthly YTD vs PY" className="rounded-sm"
                   data-testid="pivot-save-name" />
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" onClick={() => setOpenSaveSpec(false)}>Cancel</Button>
            <Button onClick={saveSpec}
                     className="bg-accent hover:bg-accent/90 text-white"
                     data-testid="pivot-save-btn">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SortableColumnRow({ column, presets, onChange, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: column.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <div ref={setNodeRef} style={style}
         className="border rounded-sm p-2 bg-card"
         data-testid={`pivot-col-${column.id}`}>
      <div className="flex items-center gap-1.5">
        <button {...attributes} {...listeners}
                className="text-muted-foreground hover:text-accent cursor-grab">
          <GripVertical className="w-3 h-3" />
        </button>
        <Select value={column.preset}
                onValueChange={(v) => onChange({ ...column, preset: v })}>
          <SelectTrigger className="rounded-sm h-7 text-xs flex-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {presets.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <button onClick={onRemove}
                className="text-muted-foreground hover:text-red-500">
          <X className="w-3 h-3" />
        </button>
      </div>
      {column.preset === "custom" && (
        <div className="grid grid-cols-2 gap-1 mt-2">
          <Input type="date" value={column.from_date || ""}
                   onChange={(e) => onChange({ ...column, from_date: e.target.value })}
                   className="rounded-sm h-7 text-xs" />
          <Input type="date" value={column.to_date || ""}
                   onChange={(e) => onChange({ ...column, to_date: e.target.value })}
                   className="rounded-sm h-7 text-xs" />
        </div>
      )}
      <Input placeholder="Custom label (optional)"
               value={column.label || ""}
               onChange={(e) => onChange({ ...column, label: e.target.value })}
               className="rounded-sm h-7 text-xs mt-1" />
    </div>
  );
}

function ManageLineItemsDialog({ open, onOpenChange, items, accounts, onChanged }) {
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [label, setLabel] = useState("");
  const [sign, setSign] = useState(1);
  const [accountIds, setAccountIds] = useState([]);
  const [formula, setFormula] = useState("");

  const startNew = () => {
    setEditing("__new"); setName(""); setLabel(""); setSign(1); setAccountIds([]); setFormula("");
  };
  const startEdit = (item) => {
    setEditing(item.id); setName(item.name);
    setLabel(item.label || ""); setSign(item.sign || 1);
    setAccountIds(item.account_ids || []); setFormula(item.formula || "");
  };

  const save = async () => {
    if (!name.trim()) return toast.error("Give the line-item a name");
    try {
      const body = { name: name.trim(), label: label.trim() || null,
                       sign, account_ids: formula.trim() ? [] : accountIds,
                       formula: formula.trim() || null };
      if (editing === "__new") {
        await api.post("/mis/pivot/line-items", body);
      } else {
        await api.patch(`/mis/pivot/line-items/${editing}`, body);
      }
      toast.success("Saved");
      setEditing(null); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this line-item?")) return;
    try {
      await api.delete(`/mis/pivot/line-items/${id}`);
      toast.success("Deleted"); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm sm:max-w-4xl">
        <DialogHeader><DialogTitle>Line-item categories</DialogTitle></DialogHeader>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="border rounded-sm p-2 max-h-[60vh] overflow-auto">
            <Button size="sm" onClick={startNew} className="w-full rounded-sm mb-2 gap-1"
                      data-testid="li-new">
              <Plus className="w-3 h-3" /> New category
            </Button>
            {items.map((it) => (
              <div key={it.id}
                     className={`p-2 rounded-sm border mb-1 cursor-pointer text-xs ${editing === it.id ? "bg-accent/10 border-accent" : "hover:bg-muted/50"}`}
                     onClick={() => startEdit(it)}
                     data-testid={`li-item-${it.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium">{it.label || it.name}</span>
                  <button onClick={(e) => { e.stopPropagation(); remove(it.id); }}
                          className="text-red-500 opacity-60 hover:opacity-100">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {it.account_ids?.length || 0} accounts · sign {it.sign || 1}
                  {it.is_system_default ? " · default" : ""}
                </div>
              </div>
            ))}
          </div>
          <div className="border rounded-sm p-3 max-h-[60vh] overflow-auto">
            {!editing ? (
              <div className="text-xs italic text-muted-foreground text-center py-8">
                Pick a category to edit or create a new one.
              </div>
            ) : (
              <div className="space-y-2">
                <div>
                  <Label className="text-xs">Name (unique)</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)}
                           className="rounded-sm h-8" data-testid="li-name" />
                </div>
                <div>
                  <Label className="text-xs">Display label</Label>
                  <Input value={label} onChange={(e) => setLabel(e.target.value)}
                           className="rounded-sm h-8" data-testid="li-label" />
                </div>
                <div>
                  <Label className="text-xs">Sign</Label>
                  <Select value={String(sign)} onValueChange={(v) => setSign(Number(v))}>
                    <SelectTrigger className="rounded-sm h-8" data-testid="li-sign">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">+1 (add)</SelectItem>
                      <SelectItem value="-1">-1 (subtract)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs flex items-center gap-1">
                    <Sigma className="w-3 h-3" /> Formula (optional — overrides accounts below)
                  </Label>
                  <Input value={formula} onChange={(e) => setFormula(e.target.value)}
                           placeholder="e.g. = @cogs + @manufacturing - @rebates"
                           className="rounded-sm h-8 font-mono text-xs" data-testid="li-formula" />
                  <div className="text-[10px] text-muted-foreground mt-0.5">
                    Reference other categories by <code>@name</code>. Sign multiplies (e.g. <code>- @rebates</code> subtracts).
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Accounts in this category</Label>
                  <div className={`max-h-56 overflow-auto border rounded-sm p-2 mt-1 space-y-1 ${formula.trim() ? "opacity-40 pointer-events-none" : ""}`}>
                    {accounts.map(a => (
                      <label key={a.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-muted/50 px-1">
                        <Checkbox
                          checked={accountIds.includes(a.id)}
                          onCheckedChange={(v) => {
                            if (v) setAccountIds([...accountIds, a.id]);
                            else setAccountIds(accountIds.filter(x => x !== a.id));
                          }}
                          data-testid={`li-acc-${a.code}`} />
                        <span className="font-mono text-[10px] w-14">{a.code}</span>
                        <span className="flex-1 truncate">{a.name}</span>
                        <span className="text-[10px] uppercase text-muted-foreground">{a.account_type}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <Button onClick={save} className="rounded-sm bg-accent hover:bg-accent/90 text-white w-full"
                          data-testid="li-save">
                  <Save className="w-3 h-3 mr-1" /> Save category
                </Button>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ManageDimensionsDialog({ open, onOpenChange, items, accounts, onChanged }) {
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [values, setValues] = useState([]);

  const startNew = () => { setEditing("__new"); setName(""); setDescription(""); setValues([]); };
  const startEdit = (d) => {
    setEditing(d.id); setName(d.name); setDescription(d.description || "");
    setValues(d.values || []);
  };
  const save = async () => {
    if (!name.trim()) return toast.error("Give the dimension a name");
    try {
      if (editing === "__new") {
        await api.post("/mis/pivot/dimensions", { name: name.trim(), description, values });
      } else {
        await api.patch(`/mis/pivot/dimensions/${editing}`, { name: name.trim(), description, values });
      }
      toast.success("Saved"); setEditing(null); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this dimension?")) return;
    try {
      await api.delete(`/mis/pivot/dimensions/${id}`);
      toast.success("Deleted"); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm sm:max-w-4xl">
        <DialogHeader><DialogTitle>Custom reporting dimensions</DialogTitle></DialogHeader>
        <div className="text-xs text-muted-foreground -mt-2 mb-2">
          Add dimensions that don't exist as segments — e.g. <b>Partner</b>,
          <b>Channel</b>, <b>Vertical</b>, <b>Currency</b>. Each dimension has
          named values with filter rules (account list) that let you group
          the pivot on them.
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="border rounded-sm p-2 max-h-[60vh] overflow-auto">
            <Button size="sm" onClick={startNew} className="w-full rounded-sm mb-2 gap-1"
                      data-testid="dim-new">
              <Plus className="w-3 h-3" /> New dimension
            </Button>
            {items.map((d) => (
              <div key={d.id}
                     className={`p-2 rounded-sm border mb-1 cursor-pointer text-xs ${editing === d.id ? "bg-accent/10 border-accent" : "hover:bg-muted/50"}`}
                     onClick={() => startEdit(d)}
                     data-testid={`dim-item-${d.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium">{d.name}</span>
                  <button onClick={(e) => { e.stopPropagation(); remove(d.id); }}
                          className="text-red-500 opacity-60 hover:opacity-100">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
                <div className="text-[10px] text-muted-foreground">{d.values?.length || 0} values</div>
              </div>
            ))}
          </div>
          <div className="border rounded-sm p-3 max-h-[60vh] overflow-auto">
            {!editing ? (
              <div className="text-xs italic text-muted-foreground text-center py-8">
                Pick a dimension to edit or create a new one.
              </div>
            ) : (
              <div className="space-y-2">
                <div>
                  <Label className="text-xs">Name</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)}
                           className="rounded-sm h-8" data-testid="dim-name" />
                </div>
                <div>
                  <Label className="text-xs">Description</Label>
                  <Textarea value={description} onChange={(e) => setDescription(e.target.value)}
                              rows={2} className="rounded-sm text-xs" data-testid="dim-desc" />
                </div>
                <div>
                  <Label className="text-xs">Values</Label>
                  <div className="space-y-1 mt-1 max-h-56 overflow-auto">
                    {values.map((v, i) => (
                      <div key={i} className="border rounded-sm p-2 text-xs">
                        <div className="flex items-center gap-2">
                          <Input placeholder="Value label"
                                   value={v.label || ""}
                                   onChange={(e) => setValues(values.map((x, j) => j === i ? { ...x, label: e.target.value } : x))}
                                   className="rounded-sm h-7 text-xs flex-1" />
                          <button onClick={() => setValues(values.filter((_, j) => j !== i))}
                                    className="text-red-500 hover:opacity-100">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                        <details className="mt-1">
                          <summary className="text-[10px] font-mono uppercase cursor-pointer text-muted-foreground">
                            Accounts filter ({(v.filter?.account_ids || []).length})
                          </summary>
                          <div className="max-h-32 overflow-auto border rounded-sm mt-1 p-1">
                            {accounts.map(a => (
                              <label key={a.id} className="flex items-center gap-1 text-[10px] hover:bg-muted/50 px-1">
                                <Checkbox
                                  checked={(v.filter?.account_ids || []).includes(a.id)}
                                  onCheckedChange={(chk) => {
                                    const acc = v.filter?.account_ids || [];
                                    const next = chk ? [...acc, a.id] : acc.filter(x => x !== a.id);
                                    setValues(values.map((x, j) => j === i ? { ...x, filter: { ...(x.filter || {}), account_ids: next } } : x));
                                  }} />
                                <span className="font-mono w-14">{a.code}</span>
                                <span className="flex-1 truncate">{a.name}</span>
                              </label>
                            ))}
                          </div>
                        </details>
                      </div>
                    ))}
                    <Button size="sm" variant="outline"
                              onClick={() => setValues([...values, { label: "", filter: {} }])}
                              className="rounded-sm h-7 w-full gap-1" data-testid="dim-add-val">
                      <Plus className="w-3 h-3" /> Add value
                    </Button>
                  </div>
                </div>
                <Button onClick={save} className="rounded-sm bg-accent hover:bg-accent/90 text-white w-full"
                          data-testid="dim-save">
                  <Save className="w-3 h-3 mr-1" /> Save dimension
                </Button>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
